import { useMemo, useState } from 'react'
import classes from './ChatWidget.module.scss'
import { ChatAddress } from './types'
import { addressBody, addressCopyText, addressHeading, addressMeta, resolveAddressView } from './addresses'

interface Props {
  addresses: ChatAddress[]
}

/**
 * Bayinin kayıtlı adres kartı: Fatura/Sevk sekmesi + satır bazında kopyalama.
 * Tek tür geldiyse sekme çubuğu çizilmez (bilgi taşımaz — sayaç zaten türü söylüyor).
 * Türetilmiş durumun tamamı `resolveAddressView`'da hesaplanır (bkz. addresses.ts) — bu bileşen
 * yalnız sunum ve iki `useState`'ten ibarettir.
 * Gelen veri olduğu gibi yansıtılır: adres doğrulanmaz, satır gizlenmez.
 */
export default function AddressCards({ addresses }: Props) {
  const [tabIndex, setTabIndex] = useState(0)
  const [copied, setCopied] = useState<{ key: string; failed: boolean } | null>(null)

  const view = useMemo(() => resolveAddressView(addresses, tabIndex), [addresses, tabIndex])

  if (addresses.length === 0 || !view.activeTab) return null

  const { tabs, activeTab, countLabel } = view

  async function copyAddress(key: string, text: string) {
    try {
      await navigator.clipboard.writeText(text)
      setCopied({ key, failed: false })
    } catch {
      setCopied({ key, failed: true })
    }
    window.setTimeout(() => setCopied(current => (current?.key === key ? null : current)), 2000)
  }

  return (
    <div className={classes.addrCard}>
      {tabs.length > 1 && (
        <div className={classes.addrTabs}>
          {tabs.map((tab, index) => (
            <button
              key={tab.kind}
              type='button'
              aria-pressed={index === tabIndex}
              className={`${classes.addrTab} ${index === tabIndex ? classes.addrTabActive : ''}`}
              onClick={() => setTabIndex(index)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className={classes.addrCount}>{countLabel}</div>

      {activeTab.addresses.map((address, index) => {
        // Aynı adres metni tekrar edebilir (backend tekilleştirmesi tür bazında); indeks anahtarı sabitler.
        const key = `${address.kind}-${index}`
        const meta = addressMeta(address)
        const contactInfo = [address.phone, address.email].filter(Boolean).join(' · ')

        return (
          <div key={key} className={classes.addrRow}>
            <div className={classes.addrHead}>
              <span className={classes.addrTitle}>{addressHeading(address)}</span>
              {address.isPreferred && <span className={classes.addrBadge}>Varsayılan</span>}
            </div>
            {address.contactName && <div className={classes.addrContact}>{address.contactName}</div>}
            <div className={classes.addrBody}>{addressBody(address)}</div>
            {meta && <div className={classes.addrMeta}>{meta}</div>}
            <div className={classes.addrFoot}>
              <span className={classes.addrContactInfo}>{contactInfo}</span>
              <button
                type='button'
                className={classes.addrCopyBtn}
                onClick={() => copyAddress(key, addressCopyText(address))}
              >
                {copied?.key === key ? (copied.failed ? 'Kopyalanamadı' : 'Kopyalandı') : 'Kopyala'}
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
